import { useEffect, useRef } from "react";
import {
  keepPreviousData,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { detailPollMs } from "@/components/main/apps/app-format";
import { appKeys } from "./keys";
import type { McpPeriod } from "@/lib/api/organizations";
import {
  answerQuestion,
  deleteQuestion,
  appAction,
  askingStep,
  AppApiError,
  applyDesign,
  syncDesign,
  renameDraftColumn,
  setDraftUnit,
  confirmAllPicks,
  createDesign,
  deleteApp,
  deleteProviderKey,
  fetchApp,
  fetchModelTables,
  fetchMySpend,
  fetchProviderKeyUsage,
  putMemberLimit,
  putPaidSearches,
  putProviderLimit,
  putSearchLimit,
  fetchProviders,
  fetchQuestions,
  fetchRun,
  fetchSchemaSourceTables,
  fetchTableLinks,
  listApps,
  putProviderKey,
  sendDesignFeedback,
  saveDesignSetup,
  stopApp,
} from "./apps";
import type { DesignSetupChange, TableLink } from "./apps";
import type {
  AnswerRequest,
  AskingRequest,
  AppDetail,
  AppListItem,
  AppPage,
  DesignApp,
  DraftRound,
  DraftTable,
  ModelTable,
  ProviderCatalog,
  ProviderKeyUsage,
  MySpend,
  QuestionsResponse,
  RunDetail,
} from "./types";

export function useApps(orgId: string | null): UseQueryResult<AppListItem[]> {
  return useQuery({
    queryKey: appKeys.list(orgId ?? ""),
    queryFn: () => listApps(orgId ?? ""),
    enabled: Boolean(orgId),
  });
}

/** An app's page, whatever its kind: what the app route reads to pick one. */
export function useAppPage(appId: string | null): UseQueryResult<AppPage> {
  return useQuery({
    queryKey: appKeys.detail(appId ?? ""),
    queryFn: () => fetchApp(appId ?? ""),
    enabled: Boolean(appId),
  });
}

/** A research app; polls while a run is live or due, so the page sees a run
 *  begin and end without a reload. */
export function useApp(
  appId: string | null,
): UseQueryResult<AppDetail | undefined> {
  return useQuery({
    queryKey: appKeys.detail(appId ?? ""),
    queryFn: () => fetchApp(appId ?? ""),
    enabled: Boolean(appId),
    select: (page: AppPage) => (page.kind === "research" ? page : undefined),
    refetchInterval: (query) =>
      query.state.data?.kind === "research"
        ? detailPollMs(query.state.data)
        : false,
  });
}

const DRAFTING_POLL_MS = 3000;

/** A design app; re-read on every open and polled while a round drafts, so
 *  a page left mid-round and opened again sees the version land. */
export function useDesignApp(
  appId: string | null,
): UseQueryResult<DesignApp | undefined> {
  return useQuery({
    queryKey: appKeys.detail(appId ?? ""),
    queryFn: () => fetchApp(appId ?? ""),
    enabled: Boolean(appId),
    select: (page: AppPage) => (page.kind === "design" ? page : undefined),
    refetchOnMount: "always",
    refetchInterval: (query) =>
      query.state.data?.kind === "design" && query.state.data.drafting
        ? DRAFTING_POLL_MS
        : false,
  });
}

/** `live` polls, while a round may file questions. */
export function useQuestions(
  appId: string | null,
  live = false,
): UseQueryResult<QuestionsResponse> {
  return useQuery({
    queryKey: appKeys.questions(appId ?? ""),
    queryFn: () => fetchQuestions(appId ?? ""),
    enabled: Boolean(appId),
    refetchInterval: live ? 2_000 : false,
  });
}

/** The links of each table, for a graph of them. */
export function useTableLinks(tableIds: string[]): TableLink[] {
  const results = useQueries({
    queries: tableIds.map((id) => ({
      queryKey: [...appKeys.all, "links", id],
      queryFn: () => fetchTableLinks(id),
      staleTime: 60_000,
    })),
  });
  return results.flatMap((r) => r.data ?? []);
}

/** One step of any app, polled while it is live: `pollMs` for a round a
 *  person watches, else every 5 s. */
export function useRun(
  appId: string | null,
  runId: string | null,
  live: boolean,
  pollMs = 5_000,
): UseQueryResult<RunDetail> {
  const query = useQuery({
    queryKey: appKeys.run(appId ?? "", runId ?? ""),
    queryFn: () => fetchRun(appId ?? "", runId ?? ""),
    enabled: Boolean(appId && runId),
    refetchInterval: live ? pollMs : false,
  });
  // The run's last lines land between the final poll and the app reporting
  // it over; one more read once it is no longer live picks them up.
  const { refetch } = query;
  const wasLive = useRef(live);
  useEffect(() => {
    if (wasLive.current && !live) void refetch();
    wasLive.current = live;
  }, [live, refetch]);
  return query;
}

/** A write to a research app: every apps query reads again after it. */
function useAppsWrite<A = void, R = unknown>(write: (arg: A) => Promise<R>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: write,
    onSuccess: () => void client.invalidateQueries({ queryKey: appKeys.all }),
  });
}

/** A write that answers with the design as it now is. */
function useDesignWrite<A = void>(
  appId: string,
  write: (arg: A) => Promise<DesignApp>,
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: write,
    onSuccess: (app: DesignApp) =>
      client.setQueryData(appKeys.detail(appId), app),
  });
}

export function useAppAction(
  appId: string,
  action: "pause" | "resume" | "delete",
) {
  return useAppsWrite(async () => {
    if (action !== "delete") return appAction(appId, action);
    await deleteApp(appId);
    return null;
  });
}

export function useDeleteQuestion(appId: string) {
  return useAppsWrite((questionId: string) =>
    deleteQuestion(appId, questionId),
  );
}

export function useAnswerQuestion(appId: string) {
  return useAppsWrite(
    ({ questionId, answer }: { questionId: string; answer: AnswerRequest }) =>
      answerQuestion(appId, questionId, answer),
  );
}

/** The one asking door (docs/ResearchAppSetup.md § 4). */
export function useAsking(appId: string) {
  return useAppsWrite((request: AskingRequest) => askingStep(appId, request));
}

export function useConfirmAll(appId: string) {
  return useAppsWrite(() => confirmAllPicks(appId));
}

/** Spend has no websocket push, so a page that shows it polls while open. */
const SPEND_POLL_MS = 15_000;

/** `live` polls, for a page that shows the keys' spend. */
export function useProviders(
  orgId: string | null,
  options: { live?: boolean } = {},
): UseQueryResult<ProviderCatalog> {
  return useQuery({
    queryKey: appKeys.providers(orgId ?? ""),
    queryFn: () => fetchProviders(orgId ?? ""),
    enabled: Boolean(orgId),
    staleTime: options.live ? 0 : 60_000,
    refetchInterval: options.live ? SPEND_POLL_MS : false,
  });
}

export function useProviderKeyUsage(
  orgId: string | null,
  period: McpPeriod,
): UseQueryResult<ProviderKeyUsage> {
  return useQuery({
    queryKey: appKeys.keyUsage(orgId ?? "", period),
    queryFn: () => fetchProviderKeyUsage(orgId ?? "", period),
    enabled: Boolean(orgId),
    placeholderData: keepPreviousData,
    staleTime: 0,
    refetchInterval: SPEND_POLL_MS,
  });
}

/** What reads an org's keys besides the catalog: the usage view lists the
 *  keys it holds, and a member's own figure breaks down by key. */
function invalidateSpend(
  client: ReturnType<typeof useQueryClient>,
  orgId: string,
) {
  void client.invalidateQueries({ queryKey: appKeys.keyUsage(orgId) });
  void client.invalidateQueries({ queryKey: appKeys.mySpend(orgId) });
}

export function useProviderKey(orgId: string) {
  const client = useQueryClient();
  const refresh = (catalog: ProviderCatalog) => {
    client.setQueryData(appKeys.providers(orgId), catalog);
    invalidateSpend(client, orgId);
  };
  const put = useMutation({
    mutationFn: ({ provider, apiKey }: { provider: string; apiKey: string }) =>
      putProviderKey(provider, orgId, apiKey),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (provider: string) => deleteProviderKey(provider, orgId),
    onSuccess: refresh,
  });
  const limit = useMutation({
    mutationFn: ({
      provider,
      limitCents,
    }: {
      provider: string;
      limitCents: number | null;
    }) => putProviderLimit(provider, orgId, limitCents),
    onSuccess: refresh,
  });
  return { put, remove, limit };
}

export function usePaidSearches(orgId: string) {
  const client = useQueryClient();
  const refresh = (catalog: ProviderCatalog) =>
    client.setQueryData(appKeys.providers(orgId), catalog);
  const toggle = useMutation({
    mutationFn: ({
      on,
      centsPer100,
    }: {
      on: boolean;
      centsPer100: number | null;
    }) => putPaidSearches(orgId, on, centsPer100),
    onSuccess: refresh,
  });
  const limit = useMutation({
    mutationFn: (limit: number | null) => putSearchLimit(orgId, limit),
    onSuccess: refresh,
  });
  return { toggle, limit };
}

export function useMemberLimit(orgId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      clientId,
      limitCents,
    }: {
      clientId: string | null;
      limitCents: number | null;
    }) => putMemberLimit(orgId, clientId, limitCents),
    onSuccess: () => invalidateSpend(client, orgId),
  });
}

export function useMySpend(orgId: string | null): UseQueryResult<MySpend> {
  return useQuery({
    queryKey: appKeys.mySpend(orgId ?? ""),
    queryFn: () => fetchMySpend(orgId ?? ""),
    enabled: Boolean(orgId),
    staleTime: 0,
    refetchInterval: SPEND_POLL_MS,
  });
}

export function useCreateDesign() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: createDesign,
    onSuccess: () => void client.invalidateQueries({ queryKey: appKeys.all }),
  });
}

export function useDesignFeedback(appId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (round: DraftRound) => sendDesignFeedback(appId, round),
    onSuccess: () =>
      void client.invalidateQueries({ queryKey: appKeys.detail(appId) }),
    // The model changed: the server recorded it as the latest version, with
    // these marks on it, so the page shows that version to send again.
    onError: (e) => {
      if (e instanceof AppApiError && e.code === "model_changed")
        void client.invalidateQueries({ queryKey: appKeys.detail(appId) });
    },
  });
}

export function useRenameDraftColumn(appId: string) {
  return useDesignWrite(
    appId,
    (rename: { table: string; column: string; name: string }) =>
      renameDraftColumn(appId, rename),
  );
}

export function useSaveDesignSetup(appId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      app,
      change,
    }: {
      app: DesignApp;
      change: DesignSetupChange;
    }) => saveDesignSetup(app, change),
    // A save that lost to another one still needs the setup as it is now.
    onSettled: () =>
      client.invalidateQueries({ queryKey: appKeys.detail(appId) }),
  });
}

export function useSetDraftUnit(appId: string) {
  return useDesignWrite(appId, (pick: Parameters<typeof setDraftUnit>[1]) =>
    setDraftUnit(appId, pick),
  );
}

/** Once per page: a model someone changed becomes the latest version. */
export function useSyncDesign(appId: string) {
  return useDesignWrite(appId, () => syncDesign(appId));
}

export function useApplyDesign(appId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (interfere?: boolean) => applyDesign(appId, interfere),
    onSuccess: (app) => {
      client.setQueryData(appKeys.detail(appId), app);
      void client.invalidateQueries({ queryKey: appKeys.list(app.org_id) });
    },
  });
}

export function useModelTables(
  orgId: string | null,
  modelId: string | null,
): UseQueryResult<ModelTable[]> {
  return useQuery({
    queryKey: appKeys.modelTables(orgId ?? "", modelId ?? ""),
    queryFn: () => fetchModelTables(orgId ?? "", modelId ?? ""),
    enabled: Boolean(orgId && modelId),
  });
}

export function useSchemaSourceTables(
  orgId: string | null,
  modelId: string | null,
): UseQueryResult<DraftTable[]> {
  return useQuery({
    queryKey: appKeys.sourceTables(orgId ?? "", modelId ?? ""),
    queryFn: () => fetchSchemaSourceTables(orgId ?? "", modelId ?? ""),
    enabled: Boolean(orgId && modelId),
  });
}

export function useStopApp(appId: string | null) {
  return useMutation({
    mutationFn: () => stopApp(appId ?? ""),
  });
}
